// react
import { useCallback, useEffect, useMemo, useRef } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// feature-hooks
import useEnergyGroupUnit from '@/features/energyxplorer/hooks/useEnergyGroupType';
import useResponsiveChartHeight from '@/features/energyxplorer/hooks/useResponsiveChartHeight';

// components
import { formatNumber } from '@/utils/formatters';

// relative
import {
  darkTheme,
  lightTheme,
} from '../../../../components/highcharts/themes';

import { useTheme } from '@emotion/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/no-data-to-display';

const ChildAnalysisChart = ({
  data,
  setSeriesColors,
  availableSeriesIds,
  isLoading,
  dataSeriesAggregate,
}) => {
  const chartComponentRef = useRef(null);
  const theme = useTheme();

  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const chartHeight = useResponsiveChartHeight();
  const { unit } = useEnergyGroupUnit();

  const generateLegendName = useCallback(
    (series) => {
      const getFilterLabel = (type, value) => {
        switch (type) {
          case 'activePeriodFilter':
            switch (value) {
              case true:
                return t('filter_labels.active_period');
              case false:
                return t('filter_labels.inactive_period');
              default:
                return t('filter_labels.all_periods');
            }
          case 'activeHolidaysFilter':
            switch (value) {
              case true:
                return t('filter_labels.holidays_included');
              case false:
                return t('filter_labels.holidays_excluded');
              default:
                return '';
            }
          default:
            return '';
        }
      };

      if (!series || !series.metadata) return '';

      const { activePeriodFilter, activeHolidaysFilter } =
        series.metadata || {};

      const parts = [];

      parts.push(getFilterLabel('activePeriodFilter', activePeriodFilter));

      const holidayLabel = getFilterLabel(
        'activeHolidaysFilter',
        activeHolidaysFilter,
      );

      if (holidayLabel) parts.push(holidayLabel);

      return parts.length > 0 ? parts.join(', ') : '';
    },
    [t],
  );

  // Imperative loading overlay
  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (!chart) return;
    if (isLoading) {
      chart.showLoading(`${t('common.loading_data')}`);
      chart.hideNoData();
    } else chart.hideLoading();
  }, [isLoading, t]);

  // Build categories and chart series only when `data` changes
  const { allChildNames, chartSeries } = useMemo(() => {
    if (!data?.length) return { allChildNames: [], chartSeries: [] };

    const allChildNames = Array.from(
      new Set(
        data.flatMap((series) =>
          series.ChildNodesSummaries?.map((child) => child.Name),
        ),
      ),
    );

    const chartSeries = data.map((series) => {
      const childNameToValue = Object.fromEntries(
        series.ChildNodesSummaries.map((child) => [
          child.Name,
          child?.[dataSeriesAggregate?.value] ?? 0,
        ]),
      );
      const orderedData = allChildNames.map(
        (name) => childNameToValue[name] ?? 0,
      );
      return {
        name: generateLegendName(series),
        data: orderedData,
      };
    });

    return { allChildNames, chartSeries };
  }, [data, dataSeriesAggregate?.value, generateLegendName]);

  const options = useMemo(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;

    return {
      lang: {
        noData: t('common.no_data_available'),
      },
      noData: {
        style: {
          fontWeight: 'normal',
          color: theme.palette.mode === 'dark' ? 'lightgray' : 'gray',
        },
      },
      accessibility: {
        enabled: false,
      },
      loading: {
        labelStyle: {
          fontWeight: 'normal',
          color: theme.palette.mode === 'dark' ? 'white' : 'black',
        },
        style: {
          backgroundColor: 'rgba(255, 255, 255, 0)',
        },
        showDuration: 150,
        hideDuration: 150,
      },
      chart: {
        type: 'column',
        height: chartHeight,
        spacingLeft: 40,
        spacingRight: 40,
        backgroundColor: themeOptions.chart.backgroundColor,
        events: {
          load() {
            const chart = this;

            // Ensure we don't attach multiple listeners (React rerenders / remounts)
            if (chart.__mouseleaveHandler) return;

            chart.__mouseleaveHandler = () => {
              chart.tooltip?.hide(0);
              chart.pointer?.reset?.(true); // clears hover state
              chart.xAxis?.forEach((a) => a.hideCrosshair?.());
              chart.yAxis?.forEach((a) => a.hideCrosshair?.());
            };

            chart.container.addEventListener(
              'mouseleave',
              chart.__mouseleaveHandler,
            );
          },
          destroy() {
            const chart = this;
            if (chart.__mouseleaveHandler) {
              chart.container.removeEventListener(
                'mouseleave',
                chart.__mouseleaveHandler,
              );
              chart.__mouseleaveHandler = null;
            }
          },
        },
      },
      title: { text: '' },
      xAxis: {
        categories: allChildNames,
        gridLineColor: 'transparent',
        crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
        lineColor: themeOptions.xAxis.lineColor,
        labels: { style: { color: themeOptions.xAxis.labels.style.color } },
      },
      yAxis: {
        title: { text: `${t('common.energy_usage')} (${unit})` },
        gridLineColor: 'transparent',
        crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
        min: 0,
        labels: { style: { color: themeOptions.yAxis.labels.style.color } },
      },
      tooltip: {
        shared: true,
        formatter: function () {
          let tooltip = `<b>${this.x}</b><br/>`;
          this.points?.forEach((point) => {
            tooltip += `<span style="color:${point.color}">\u25CF</span> ${
              point.series.name
            }: <b>${formatNumber(point.y)} ${unit}</b><br/>`;
          });
          return tooltip;
        },
        backgroundColor: themeOptions.tooltip.backgroundColor,
        borderColor: themeOptions.tooltip.borderColor,
        style: {
          color: theme.palette.mode === 'dark' ? 'white' : 'black',
        },
      },
      plotOptions: {
        column: {
          grouping: true,
          borderWidth: 0,
        },
      },
      legend: {
        itemStyle: { color: themeOptions.legend.itemStyle.color },
      },
      series: chartSeries,
      credits: { enabled: false },
    };
  }, [allChildNames, chartHeight, chartSeries, t, theme.palette.mode, unit]);

  useEffect(() => {
    if (typeof setSeriesColors !== 'function') return;
    if (!availableSeriesIds?.size) return;

    const chart = chartComponentRef.current?.chart;
    if (!chart || !chart.series?.length) return;

    // Sort the indices that actually have data (0,2,...) so mapping is stable.
    const indicesWithData = Array.from(availableSeriesIds).sort(
      (a, b) => a - b,
    );

    // Build an array aligned to filterObjects indices:
    // seriesColorsByIndex[filterIndex] = { name, color }
    const seriesColorsByIndex = [];

    indicesWithData.forEach((filterIndex, producedSeriesPos) => {
      const s = chart.series[producedSeriesPos];
      if (!s) return;
      seriesColorsByIndex[filterIndex] = { name: s.name, color: s.color };
    });

    setSeriesColors(seriesColorsByIndex);
  }, [availableSeriesIds, chartSeries, setSeriesColors]);

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      ref={chartComponentRef}
    />
  );
};

export default ChildAnalysisChart;
